import { useEffect, useRef, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { usePrimaryComplaintStore } from "@/store/primaryComplaint";
import { complaintHistoryLabels as t } from "@/components/complaints/complaintHistoryLabels";
import { Button } from "@/components/ui/Button";
import { WizardActionsBar } from "@/components/wizard/WizardActionsBar";
import { ChoiceGroup } from "@/components/forms/ChoiceGroup";
import { ComplaintStepHeader, StepLoadError } from "@/components/complaints/ComplaintStepHeader";
import { useComplaintValues } from "@/hooks/useComplaintValues";
import { missingRequired } from "@/utils/complaintSteps";
import { useTaskStore } from "@/store/task";
import { askVasFor } from "@/types/task-actions";

/**
 * Headaches visit. Ported from the app-pwa HeadacheHistory.vue design — which never actually
 * saved anything — so here the answers are mapped onto complaint fields that already exist and
 * already reach the clinician:
 *   intensity  → CurrentSeverity
 *   pain type  → Qualities (comma-joined)
 *   duration   → Onset
 *   frequency  → Frequency
 *   location + a readable summary → Story (the visit note)
 * Continue → outcome assessment.
 */
const Fields = {
  ONSET: "Onset",
  FREQUENCY: "Frequency",
  QUALITIES: "Qualities",
  CURRENT_SEVERITY: "CurrentSeverity",
  SLEEP: "Sleep",
  WORK: "Work",
  AGGS: "Aggs",
  RELIEF: "Relief",
  STORY: "Story",
} as const;

const options = (list: readonly string[]) => list.map((o) => ({ value: o, label: o }));
const DURATION_OPTIONS = options(["A few days", "A few weeks", "Several months", "Years", "Since childhood"]);
const FREQUENCY_OPTIONS = options(["Daily", "Several times a week", "Weekly", "Occasionally", "Monthly", "Seasonal"]);
const LOCATION_OPTIONS = options(["Front", "Behind the eyes", "Back of head", "Sides", "Whole head", "Neck & head"]);
const TYPE_OPTIONS = options(["Dull ache", "Throbbing/pulsating", "Pressure", "Stabbing/sharp", "Burning"]);
const WORSE_OPTIONS = options(["Bright light", "Loud noise", "Stress", "Lack of sleep", "Screen time", "Certain foods/caffeine", "Physical activity"]);
const BETTER_OPTIONS = options(["Rest", "Dark room", "Sleep", "Medication", "Hydration", "Caffeine"]);
const scale = (from: number, to: number) =>
  Array.from({ length: to - from + 1 }, (_, i) => String(from + i)).map((n) => ({ value: n, label: n }));
const SEVERITY_OPTIONS = scale(1, 10);
const IMPACT_OPTIONS = scale(0, 10);
/** A stored rating in range, or null (unanswered). Severity 0 was the old slider default, not an answer. */
const rating = (raw: string | undefined, min: number) => {
  const n = Number((raw ?? "").trim());
  return raw != null && raw.trim() !== "" && Number.isInteger(n) && n >= min && n <= 10 ? String(n) : null;
};
const list = (raw: string | undefined) => (raw ? raw.split(",").filter(Boolean) : []);

function buildStory(location: string[], type: string[], frequency: string, duration: string): string {
  const parts: string[] = [];
  if (location.length) parts.push(`Location: ${location.join(", ")}`);
  if (type.length) parts.push(`Type: ${type.join(", ")}`);
  if (frequency) parts.push(`Frequency: ${frequency}`);
  if (duration) parts.push(`Duration: ${duration}`);
  return parts.length ? `Headache details — ${parts.join("; ")}.` : "";
}

export function HeadacheHistoryPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const complaintId = searchParams.get("complaintId");

  const updateComplaintValue = usePrimaryComplaintStore((s) => s.updateComplaintValue);
  // Loads by complaint id alone, so a missing patientID can no longer skip the load (and Continue
  // can no longer save a blank form over the patient's answers).
  const { values, status, retry } = useComplaintValues(complaintId);
  // The case history config can turn the 1-10 rating off; it is then neither asked nor saved.
  const askVas = useTaskStore((s) => askVasFor(s.portalConfigMeta));

  const [duration, setDuration] = useState<string | null>(null);
  const [frequency, setFrequency] = useState<string | null>(null);
  const [location, setLocation] = useState<string[]>([]);
  const [types, setTypes] = useState<string[]>([]);
  const [worse, setWorse] = useState<string[]>([]);
  const [better, setBetter] = useState<string[]>([]);
  // Ratings start unanswered (null) and are only saved once the patient picks one.
  const [severity, setSeverity] = useState<string | null>(null);
  const [sleep, setSleep] = useState<string | null>(null);
  const [work, setWork] = useState<string | null>(null);
  // Fields whose last save failed. One field saving must not hide another field's failure.
  const [failedSaves, setFailedSaves] = useState<ReadonlySet<string>>(new Set());
  const saveError = failedSaves.size > 0;
  const [missing, setMissing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Restore previously entered answers when returning to this screen.
  useEffect(() => {
    if (status !== "ready" || !values) return;
    for (const v of values) {
      if (v.code === Fields.CURRENT_SEVERITY) setSeverity(rating(v.value, 1));
      else if (v.code === Fields.SLEEP) setSleep(rating(v.value, 0));
      else if (v.code === Fields.WORK) setWork(rating(v.value, 0));
      else if (v.code === Fields.QUALITIES) setTypes(list(v.value));
      else if (v.code === Fields.ONSET) setDuration(v.value || null);
      else if (v.code === Fields.FREQUENCY) setFrequency(v.value || null);
      else if (v.code === Fields.AGGS) setWorse(list(v.value));
      else if (v.code === Fields.RELIEF) setBetter(list(v.value));
      else if (v.code === Fields.STORY) {
        const m = /Location:\s*([^;.]+)/.exec(v.value ?? "");
        if (m) setLocation(m[1].split(",").map((s) => s.trim()).filter(Boolean));
      }
    }
  }, [status, values]);

  // Fields the patient changed on this visit (every change goes through persist).
  const touched = useRef(new Set<string>());
  const persist = async (code: string, value: string) => {
    touched.current.add(code);
    try {
      await updateComplaintValue(code, value);
      setFailedSaves((prev) => {
        if (!prev.has(code)) return prev;
        const next = new Set(prev);
        next.delete(code);
        return next;
      });
      return true;
    } catch {
      setFailedSaves((prev) => new Set(prev).add(code));
      return false;
    }
  };

  const goBack = () => navigate("/complaint/primary-complaint");

  const saveAndContinue = async () => {
    if (status !== "ready") return;
    if (missingRequired([{ id: "headache-severity", answered: !askVas || severity != null }])) {
      setMissing(true);
      const el = document.getElementById("headache-severity");
      el?.scrollIntoView({ block: "center", behavior: "smooth" });
      el?.focus({ preventScroll: true });
      return;
    }
    // Re-save every answered field, plus every field the patient changed on this visit, even when
    // now empty (a clear whose immediate save failed must not leave the old answer on the chart).
    // Untouched blanks are never written, so a skipped question never overwrites anything.
    const story = buildStory(location, types, frequency ?? "", duration ?? "");
    const saves: Promise<boolean>[] = [];
    const resave = (code: string, value: string) => {
      if (value !== "" || touched.current.has(code)) saves.push(persist(code, value));
    };
    if (askVas) resave(Fields.CURRENT_SEVERITY, severity ?? "");
    resave(Fields.SLEEP, sleep ?? "");
    resave(Fields.WORK, work ?? "");
    resave(Fields.QUALITIES, types.join(","));
    resave(Fields.ONSET, duration ?? "");
    resave(Fields.FREQUENCY, frequency ?? "");
    resave(Fields.AGGS, worse.join(","));
    resave(Fields.RELIEF, better.join(","));
    // Story is rebuilt from the answers above, so it is only written when one of them changed:
    // a saved narrative from elsewhere is never replaced by an empty or partial rebuild.
    if (touched.current.has(Fields.STORY)) saves.push(persist(Fields.STORY, story));
    setIsSaving(true);
    const results = await Promise.all(saves);
    setIsSaving(false);
    if (results.some((ok) => !ok)) return; // don't advance if any save failed
    const next = new URLSearchParams(searchParams);
    navigate({ pathname: "/complaint/outcome-assesment", search: `?${next.toString()}` });
  };

  return (
    <div className="wizard-page w-full">
      <div className="wizard-page__content p-4 flex flex-col">
        <div className="complaint-history-card">
          <ComplaintStepHeader complaintId={complaintId} step="dedicated" />

          {status === "loading" && (
            <div className="wizard-skeleton" role="status" aria-busy="true" aria-label="Loading your answers">
              <span />
              <span />
              <span />
            </div>
          )}

          {status === "error" && <StepLoadError onRetry={retry} />}

          {status === "ready" && (
            <>
              <ChoiceGroup
                id="headache-duration"
                legend="How long have you had them?"
                options={DURATION_OPTIONS}
                value={duration}
                onChange={(v) => {
                  if (v == null) return;
                  setDuration(v);
                  void persist(Fields.ONSET, v);
                  void persist(Fields.STORY, buildStory(location, types, frequency ?? "", v));
                }}
              />
              <ChoiceGroup
                id="headache-frequency"
                legend="How often do they happen?"
                options={FREQUENCY_OPTIONS}
                value={frequency}
                onChange={(v) => {
                  if (v == null) return;
                  setFrequency(v);
                  void persist(Fields.FREQUENCY, v);
                  void persist(Fields.STORY, buildStory(location, types, v, duration ?? ""));
                }}
              />
              <ChoiceGroup
                id="headache-location"
                multiple
                legend="Where do you feel it?"
                hint={`(${t.selectAllApply})`}
                options={LOCATION_OPTIONS}
                value={location}
                onChange={(next) => {
                  setLocation(next);
                  void persist(Fields.STORY, buildStory(next, types, frequency ?? "", duration ?? ""));
                }}
              />
              <ChoiceGroup
                id="headache-type"
                multiple
                legend="How would you describe the pain?"
                hint={`(${t.selectAllApply})`}
                options={TYPE_OPTIONS}
                value={types}
                onChange={(next) => {
                  setTypes(next);
                  void persist(Fields.QUALITIES, next.join(","));
                  void persist(Fields.STORY, buildStory(location, next, frequency ?? "", duration ?? ""));
                }}
              />
              <ChoiceGroup
                id="headache-worse"
                multiple
                legend="What makes them worse?"
                hint={`(${t.selectAllApply})`}
                options={WORSE_OPTIONS}
                value={worse}
                onChange={(next) => {
                  setWorse(next);
                  void persist(Fields.AGGS, next.join(","));
                }}
              />
              <ChoiceGroup
                id="headache-better"
                multiple
                legend="What makes them better?"
                hint={`(${t.selectAllApply})`}
                options={BETTER_OPTIONS}
                value={better}
                onChange={(next) => {
                  setBetter(next);
                  void persist(Fields.RELIEF, next.join(","));
                }}
              />
              {askVas && (
                <ChoiceGroup
                  id="headache-severity"
                  layout="numbers"
                  required
                  legend="How intense are they?"
                  hint="(1 = mild, 10 = severe)"
                  options={SEVERITY_OPTIONS}
                  value={severity}
                  error={missing && severity == null ? t.answerToContinue : null}
                  onChange={(v) => {
                    if (v == null) return;
                    setSeverity(v);
                    setMissing(false);
                    void persist(Fields.CURRENT_SEVERITY, v);
                  }}
                />
              )}
              <ChoiceGroup
                id="headache-sleep"
                layout="numbers"
                legend="How much do they affect your sleep?"
                hint="(0 = not at all, 10 = severely)"
                options={IMPACT_OPTIONS}
                value={sleep}
                onChange={(v) => {
                  if (v == null) return;
                  setSleep(v);
                  void persist(Fields.SLEEP, v);
                }}
              />
              <ChoiceGroup
                id="headache-work"
                layout="numbers"
                legend="How much do they affect your work?"
                hint="(0 = not at all, 10 = severely)"
                options={IMPACT_OPTIONS}
                value={work}
                onChange={(v) => {
                  if (v == null) return;
                  setWork(v);
                  void persist(Fields.WORK, v);
                }}
              />

              {saveError && (
                <p className="choice-group__error" role="alert">
                  We couldn&apos;t save your last answer. Check your connection — your selections are
                  still here; press Continue to try again.
                </p>
              )}
            </>
          )}
        </div>
      </div>
      <WizardActionsBar>
        <Button type="button" variant="outline" onClick={goBack} aria-label={t.backButton}>
          ← {t.backButton}
        </Button>
        <Button type="button" onClick={saveAndContinue} disabled={isSaving || status !== "ready"}>
          {isSaving ? "Saving…" : t.continueButton}
        </Button>
      </WizardActionsBar>
    </div>
  );
}
